import React, { useEffect, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { MdStars } from "react-icons/md";
import {
  Input,
  CustomCard,
  CustomCardImage,
  CustomCardTitle,
  Button,
  Alert,
} from "@repo/ui";
import FiltersComponent, { Filters } from "./FiltersComponent";
import {
  Product,
  ProductListParams,
  listProducts,
} from "../../../services/productApiService";
import withAnimation from "../../../hoc/withCardAnimation/withCardAnimation";
import placeholderImage from "../../../assets/placeholders/picture.webp";
import Modal from "../../../components/Modal/Modal";
import { addProductToGiftTable } from "../../../redux/slices/giftTableSlice";
import { RootState } from "../../../redux/store";
import { selectProductInGiftTable } from "../../../redux/selectors/gifttableSelector";
import { useAppDispatch, useAppSelector } from "../../../hooks/useAppDispatch";
import EmptyPlaceholder from "../../../components/EmptyPlaceholder/EmptyPlaceholder";
import Carousel from "../../../components/Carousel/Carousel";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";

interface CatalogContentProps {
  categoryID?: string; // Prop to receive selected category
}

const LOCALE = "modules_MyGiftTable_components_CatalogContent";
const AnimatedCard = withAnimation(CustomCard);

const CatalogContent: React.FC<CatalogContentProps> = ({ categoryID }) => {
  const { t } = useTranslation(LOCALE);

  const dispatch = useAppDispatch();
  const giftTable = useAppSelector((state: RootState) => state.giftTable);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageToken, setPageToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState<Filters>({});
  const [searchText, setSearchText] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const [lastToken, setLastToken] = useState<string | null>(null);

  const fetchProducts = useCallback(
    async (clearProducts: boolean = false) => {
      if (loading || !hasMore) return;
      setLoading(true);

      try {
        const params: ProductListParams = {
          pageToken: clearProducts ? null : pageToken,
          categoryId: categoryID || filters.category_id,
          brand: filters.brand,
          recommended: filters.recommended || undefined,
          searchText,
          tags: filters.tags,
          exclusive: filters.exclusive || undefined,
          pageSize: 20,
          price: filters.price,
          sort: filters.sort,
        };

        const newProducts = await listProducts(params);

        if (clearProducts) {
          setProducts(newProducts.products || []);
        } else {
          setProducts((prevProducts) => [
            ...prevProducts,
            ...(newProducts.products?.filter(
              (newProduct) =>
                !prevProducts.some(
                  (prevProduct) => prevProduct.id === newProduct.id
                )
            ) || []),
          ]);
        }

        setPageToken(newProducts.nextToken || null);
        setHasMore(
          newProducts.nextToken != lastToken ? !!newProducts.nextToken : false
        );
        setLastToken(newProducts.nextToken);
      } catch (error) {
        console.error(t("errorFetching"));
      } finally {
        setLoading(false);
      }
    },
    [loading, hasMore, pageToken, filters, searchText, categoryID]
  );

  useEffect(() => {
    setHasMore(true);
    fetchProducts(true);
  }, [filters, searchText, categoryID]);

  useEffect(() => {
    const sentinel = document.getElementById("scroll-sentinel");

    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        console.log(entries[0].isIntersecting, hasMore, loading);
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchProducts();
        }
      },
      { rootMargin: "200px" } // Trigger when 200px away from the viewport
    );

    observer.observe(sentinel);

    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [fetchProducts, hasMore, loading]);

  const clearSearch = () => {
    setSearchText("");
    setFilters({});
    setPageToken(null);
    setHasMore(true);
  };

  const onApplyFilters = (filters: Filters) => {
    setFilters(filters);
  };

  const handleCardClick = (product: Product) => {
    const existingProduct = selectProductInGiftTable(giftTable, product.id);
    if (existingProduct) {
      if (existingProduct) setAlert(t("alreadyAdded"));
    }
    setSelectedProduct(product);
    setQuantity(1);
    setHoveredCard(null); // Reset hover state
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setAlert(null);
  };

  const incrementQuantity = () => setQuantity((prev) => prev + 1);
  const decrementQuantity = () => setQuantity((prev) => Math.max(1, prev - 1));

  const addToGiftTable = (priority: boolean, isPhysical = false) => {
    if (selectedProduct) {
      Sentry.addBreadcrumb({
        category: "my_table.catalog",
        message: "Owner confirmed adding a catalog item",
        level: "info",
        data: { operation: "add_product", quantity, priority, isPhysical },
      });
      dispatch(
        addProductToGiftTable({
          productID: selectedProduct.id,
          category: selectedProduct.category,
          categoryID: selectedProduct.category_id,
          brand: selectedProduct.brand,
          quantity,
          taken: 0,
          priority,
          isPhysical: isPhysical,
          value: selectedProduct.price,
        })
      );
      closeModal();
    }
  };

  const getProductCoverImage = (product: Product): string => {
    return (
      product.cover_picture?.trim() ||
      product.pictures?.[0]?.trim() ||
      placeholderImage
    );
  };

  return (
    <>
      <div className="mb-4">
        <div className="flex flex-col md:flex-row md:space-x-4 space-y-2 md:space-y-0">
          <div className="flex flex-1 min-w-[200px] items-center space-x-2">
            <Input
              placeholder={t("searchPlaceholder")}
              value={searchText}
              className="flex-1"
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPageToken(null);
                  setHasMore(true);
                  fetchProducts(true);
                } else if (e.key === "Escape") {
                  clearSearch();
                }
              }}
              onClear={clearSearch}
            />
            <div className="md:hidden">
              <FiltersComponent
                onApplyFilters={onApplyFilters}
                initialCategory={categoryID}
              />
            </div>
          </div>
          <div className="hidden md:flex space-x-4">
            <FiltersComponent
              onApplyFilters={onApplyFilters}
              initialCategory={categoryID}
            />
          </div>
        </div>
      </div>

      {products.length === 0 && !loading ? (
        <EmptyPlaceholder
          title={t("noProducts.title")}
          description={t("noProducts.description")}
        />
      ) : (
        <div
          className="grid gap-8"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          {products.map((product) => {
            const coverImage = getProductCoverImage(product);
            const existingProduct = selectProductInGiftTable(
              giftTable,
              product.id
            );
            return (
              <div
                key={product.id}
                onMouseEnter={() => setHoveredCard(product.id)}
                onMouseLeave={() => setHoveredCard(null)}
                className="relative group cursor-pointer"
              >
                <AnimatedCard className="relative rounded-s shadow-none border transition-transform transform group-hover:scale-102 group-hover:shadow-lg">
                  {product.exclusive && (
                    <div className="absolute top-2 left-2 bg-secondary text-white text-xs font-semibold px-2 py-1 rounded">
                      {t("exclusive")}
                    </div>
                  )}
                  {existingProduct?.priority && (
                    <MdStars className="absolute top-2 left-2 h-6 w-6 text-secondary" />
                  )}
                  <CustomCardImage
                    src={coverImage}
                    alt={product.title}
                    className="w-full h-64 md:h-96 object-contain"
                  />
                  <div className="p-4">
                    <p className="text-lg font-bold text-primary">
                      {product.brand}
                    </p>
                    <CustomCardTitle>{product.title}</CustomCardTitle>
                    <p className="text-lg font-semibold text-primary mt-2">
                      {product.display_price?.toLocaleString("es-MX", {
                        style: "currency",
                        currency: "MXN",
                      })}
                    </p>
                  </div>
                  {existingProduct && (
                    <div className="absolute top-2 right-2 bg-primary text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
                      {existingProduct.quantity}
                    </div>
                  )}
                  {hoveredCard === product.id && (
                    <div className="absolute inset-0 bg-white/75 flex flex-col items-center justify-center p-4">
                      <Button
                        label={t("addToTable")}
                        onClick={() => handleCardClick(product)}
                      />
                    </div>
                  )}
                </AnimatedCard>
              </div>
            );
          })}
        </div>
      )}

      {loading && <p>{t("loading")}</p>}

      {selectedProduct && (
        <Modal isOpen={!!selectedProduct} onClose={closeModal}>
          {alert && <Alert message={alert} level="info" />}

          {selectedProduct.exclusive && (
            <div className="absolute top-4 left-4 bg-secondary text-white text-xs font-semibold px-2 py-1 rounded">
              {t("exclusive")}
            </div>
          )}

          <h2 className="text-center font-bold text-xl">
            {selectedProduct.title}
          </h2>

          <p className="text-center my-4">
            {selectedProduct.exclusive
              ? t("exclusiveMessage")
              : t("standardMessage")}
          </p>

          <div className="relative mt-4">
            {selectedProduct.pictures?.length > 0 ? (
              <Carousel className="w-full">
                {selectedProduct.pictures.map((picture, index) => (
                  <img
                    key={index}
                    src={picture}
                    alt={`Slide ${index}`}
                    className="w-full h-96 object-contain"
                  />
                ))}
              </Carousel>
            ) : (
              <img
                src={placeholderImage}
                alt={t("noImage")}
                className="w-full h-96 object-contain"
              />
            )}
          </div>

          <div className="w-full border p-4 mt-8">
            <div className="flex justify-between items-center">
              <span>{t("quantity")}</span>
              <div className="flex items-center">
                <button
                  onClick={decrementQuantity}
                  className="px-4 py-2 text-lg bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  -
                </button>
                <span className="mx-4 font-bold">{quantity}</span>
                <button
                  onClick={incrementQuantity}
                  className="px-4 py-2 text-lg bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {selectedProduct.exclusive && (
            <Button
              label={t("receiveProduct")}
              onClick={() => addToGiftTable(false, true)}
              className="mt-2 w-full bg-secondary text-white font-semibold"
            />
          )}

          <Button
            label={t("addToFavorites")}
            onClick={() => addToGiftTable(true)}
            className="mt-4 w-full bg-primary text-white"
          />
          <Button
            label={t("addWithoutPriority")}
            type="secondary"
            onClick={() => addToGiftTable(false)}
            className="mt-2 w-full"
          />
        </Modal>
      )}

      <div id="scroll-sentinel" />
    </>
  );
};

export default withTranslations(LOCALE)(CatalogContent);
